import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextareaAutosize,
  Divider,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Refresh,
  Clear,
  Save,
  Cancel,
  School,
  Book,
  CalendarMonth,
  FilterList,
  CloudUpload,
  UploadFile,
  Subject as SubjectIcon,
  Visibility
} from '@mui/icons-material';

const Modules = () => {
  const [data, setData] = useState([]);
  const [periodes, setPeriodes] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [filiereNiveaux, setFiliereNiveaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [filtreAnnee, setFiltreAnnee] = useState('');
  const [filtreFiliere, setFiltreFiliere] = useState('');
  const [filtreNiveau, setFiltreNiveau] = useState('');
  const [filtrePeriode, setFiltrePeriode] = useState('');
  
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedItem, setSelectedItem] = useState(null);
  
  const [formData, setFormData] = useState({
    code: '',
    nom: '',
    periode_id: '',
    niveau_id: '',
    filiere_id: '',
    annee_universitaire_id: '',
    responsable: '',
    credits: 0,
    coefficient: 1,
    description: '',
    estActif: true
  });

  const [matieresDialogOpen, setMatieresDialogOpen] = useState(false);
  const [matieresModule, setMatieresModule] = useState(null);
  const [matieresList, setMatieresList] = useState([]);
  const [matieresLoading, setMatieresLoading] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [importData, setImportData] = useState({
    annee_universitaire_id: '',
    filiere_id: '',
    niveau_id: '',
    periode_id: '',
    csv: '',
    ecraser: false
  });
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      
      console.log('🔄 [Modules] Chargement des données...');
      
      const [modulesRes, periodesRes, niveauxRes, filieresRes, anneesRes, fnRes] = await Promise.all([
        axios.get('/api/modules', { headers }),
        axios.get('/api/periodes', { headers }),
        axios.get('/api/niveaux', { headers }),
        axios.get('/api/filieres', { headers }),
        axios.get('/api/annees', { headers }),
        axios.get('/api/filiere-niveaux', { headers })
      ]);
      
      console.log('📊 [Modules] Modules:', modulesRes.data?.length || 0);
      console.log('📊 [Modules] Périodes:', periodesRes.data?.length || 0);
      console.log('📊 [Modules] Niveaux:', niveauxRes.data?.length || 0);
      console.log('📊 [Modules] Filières:', filieresRes.data?.length || 0);
      console.log('📊 [Modules] Années:', anneesRes.data?.length || 0);
      
      if (anneesRes.data && anneesRes.data.length > 0) {
        console.log('📋 [Modules] Années reçues:', anneesRes.data.map(a => ({ 
          annee_id: a.annee_id, 
          intitule: a.intitule,
          estActive: a.estActive 
        })));
      }
      
      setData(modulesRes.data || []);
      setPeriodes(periodesRes.data || []);
      setNiveaux(niveauxRes.data || []);
      setFilieres(filieresRes.data || []);
      setAnnees(anneesRes.data || []);
      setFiliereNiveaux(fnRes.data || []);
      setError('');
    } catch (err) {
      console.error('❌ [Modules] Erreur:', err);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  // ⭐ Fonctions de recherche
  // Les identifiants viennent de Mongo et circulent en String d un cote, ObjectId de l autre :
  // une comparaison stricte echouait selon le type, et le module etait alors affiche sans
  // periode, sans niveau ni sans annee. On compare donc toujours en chaine.
  const memeId = (a, b) => a !== undefined && a !== null && b !== undefined && b !== null && String(a) === String(b);
  const getPeriode = (id) => periodes.find(p => memeId(p._id, id));
  const getNiveau = (id) => niveaux.find(n => memeId(n.niveau_id, id) || memeId(n._id, id));
  const getFiliere = (id) => filieres.find(f => memeId(f.filiere_id, id) || memeId(f._id, id));
  const getAnnee = (id) => annees.find(a => memeId(a._id, id) || memeId(a.annee_id, id));

  // Helper to get all valid ID formats for a given Annee value
  const getAnneeIds = (value) => {
    if (!value) return [];
    const annee = annees.find(a => (a.annee_id || a._id) === value);
    return annee ? [value, String(annee._id), annee.annee_id].filter(Boolean) : [value];
  };

  // Cascade filter bar
  const fbFilieres = filtreAnnee ? filieres.filter(f => getAnneeIds(filtreAnnee).includes(f.annee_id)) : filieres;
  const fbNiveaux = filtreFiliere
    ? niveaux.filter(n => filiereNiveaux.some(fn => memeId(fn.niveau_id, n.niveau_id) && memeId(fn.filiere_id, filtreFiliere)))
    : (filtreAnnee
        ? niveaux.filter(n =>
            filiereNiveaux.some(fn =>
              memeId(fn.niveau_id, n.niveau_id) &&
              filieres.some(f => memeId(f.filiere_id, fn.filiere_id) && getAnneeIds(filtreAnnee).includes(f.annee_id))
            )
          )
        : niveaux);
  // Les periodes sont dupliquees en base (le meme semestre existe plusieurs fois pour un niveau,
  // une par annee notamment). Sans deduplication, le Select affichait « Semestre 5 » deux fois et
  // React levait une erreur de cles dupliquees.
  const sansDoublon = (liste) => {
    const vus = new Set();
    return liste.filter((item) => {
      const cle = String(item._id);
      if (vus.has(cle)) return false;
      vus.add(cle);
      return true;
    });
  };
  const fbPeriodes = filtreNiveau ? sansDoublon(periodes.filter(p => memeId(p.niveau_id, filtreNiveau))) : periodes;

  // Cascade form (computed, no async needed)
  // Les filieres ne sont plus filtrees par annee : une filiere rattachee a 2026/2027 dont les
  // modules portent encore 2025/2026 doit rester selectionnable pour qu on puisse les corriger
  // module par module depuis le formulaire.
  const formFiltredFilieres = filieres;
  // Un module existant doit toujours voir sa valeur affichee, meme si elle ne correspond plus
  // aux liens filiere/niveau de l annee courante : sans cela le Select affichait vide et une
  // simple re-enregistrement ecrivait une valeur vide.
  const filiereCourante = filieres.find(f => String(f.filiere_id) === String(formData.filiere_id));
  const formFiltredFilieresAvecCourante = filiereCourante && !formFiltredFilieres.some(f => String(f.filiere_id) === String(formData.filiere_id))
    ? [...formFiltredFilieres, filiereCourante]
    : formFiltredFilieres;

  const niveauxLies = formData.filiere_id
    ? niveaux.filter(n => filiereNiveaux.some(fn => memeId(fn.niveau_id, n.niveau_id) && memeId(fn.filiere_id, formData.filiere_id)))
    : (formData.annee_universitaire_id
        ? niveaux.filter(n =>
            filiereNiveaux.some(fn =>
              memeId(fn.niveau_id, n.niveau_id) &&
              filieres.some(f => memeId(f.filiere_id, fn.filiere_id) && getAnneeIds(formData.annee_universitaire_id).includes(f.annee_id))
            )
          )
        : niveaux);
  const niveauCourant = niveaux.find(n => String(n.niveau_id) === String(formData.niveau_id));
  const formFiltredNiveaux = niveauCourant && !niveauxLies.some(n => String(n.niveau_id) === String(formData.niveau_id))
    ? [...niveauxLies, niveauCourant]
    : niveauxLies;

  const periodesDuNiveau = formData.niveau_id ? sansDoublon(periodes.filter(p => memeId(p.niveau_id, formData.niveau_id))) : periodes;
  const periodeCourante = periodes.find(p => String(p._id) === String(formData.periode_id));
  const formFiltredPeriodes = periodeCourante && !periodesDuNiveau.some(p => String(p._id) === String(formData.periode_id))
    ? [...periodesDuNiveau, periodeCourante]
    : periodesDuNiveau;

  // Les niveaux sont dupliques d une annee sur l autre (le « L3 Dev » 3 vaut pour 2025/2026, le
  // 19 pour 2026/2027). Retrouver celui de l annee choisie evite d enregistrer un module 2026/2027
  // sur un niveau 2025/2026, qui est precisement l incoherence de la filiere « L3 IRM Dév ».
  const niveauEquivalentAnnee = formData.niveau_id && niveauCourant
    ? niveaux.find(n => n.intitule === niveauCourant.intitule
      && n.niveau_id !== niveauCourant.niveau_id
      && getAnneeIds(formData.annee_universitaire_id).map(String).includes(String(n.annee_id)))
    : null;

  // Modules rattaches a la filiere filtree mais enregistres sous une autre annee. Ce sont eux qui
  // rendent une liste vide sans explication : on les compte pour proposer de les afficher.
  const modulesAnneeDifferente = (filtreFiliere && filtreAnnee)
    ? data.filter(m => memeId(m.filiere_id, filtreFiliere) && !memeId(m.annee_universitaire_id, filtreAnnee))
    : [];

  const filteredData = data.filter(item => {
    const search = searchTerm.toLowerCase();
    const periode = getPeriode(item.periode_id);
    const niveau = getNiveau(item.niveau_id);
    const filiere = getFiliere(item.filiere_id);
    const annee = getAnnee(item.annee_universitaire_id);

    if (filtreAnnee && !memeId(item.annee_universitaire_id, filtreAnnee)) return false;
    if (filtreFiliere && !memeId(item.filiere_id, filtreFiliere)) return false;
    if (filtreNiveau && !memeId(item.niveau_id, filtreNiveau)) return false;
    if (filtrePeriode && !memeId(item.periode_id, filtrePeriode)) return false;

    // Les filieres portent deux libelles : intitule (« INFORMATIQUE RESEAUX ET MULTIMEDIA
    // Développement informatique ») et libelle (« L3 IRM Dév »). La recherche portait sur le
    // premier seul, donc taper « IRM » ne trouvait rien : aucun resultat et aucun acces aux
    // modules de cette filiere.
    return (item.nom || '').toLowerCase().includes(search) ||
           (item.code || '').toLowerCase().includes(search) ||
           (item.responsable || '').toLowerCase().includes(search) ||
           (item.description || '').toLowerCase().includes(search) ||
           (periode?.nom || '').toLowerCase().includes(search) ||
           (niveau?.intitule || '').toLowerCase().includes(search) ||
           (filiere?.intitule || '').toLowerCase().includes(search) ||
           (filiere?.libelle || '').toLowerCase().includes(search) ||
           (annee?.intitule || '').toLowerCase().includes(search);
  });

  const handleOpenModal = (mode, item = null) => {
    console.log('🔄 [Modal] Ouverture - Mode:', mode);
    setModalMode(mode);
    if (item) {
      setSelectedItem(item);
      setFormData({
        code: item.code || '',
        nom: item.nom || '',
        periode_id: item.periode_id || '',
        niveau_id: item.niveau_id || '',
        filiere_id: item.filiere_id || '',
        annee_universitaire_id: item.annee_universitaire_id || '',
        responsable: item.responsable || '',
        credits: item.credits || 0,
        coefficient: item.coefficient || 1,
        description: item.description || '',
        estActif: item.estActif !== undefined ? item.estActif : true
      });
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      code: '',
      nom: '',
      periode_id: '',
      niveau_id: '',
      filiere_id: '',
      annee_universitaire_id: '',
      responsable: '',
      credits: 0,
      coefficient: 1,
      description: '',
      estActif: true
    });
  };

  const loadMatieresForModule = async (module) => {
    try {
      setMatieresLoading(true);
      setMatieresModule(module);
      const res = await axios.get(`/api/matieres/module/${module._id}`, { headers: getAuthHeaders() });
      setMatieresList(res.data || []);
      setMatieresDialogOpen(true);
    } catch (err) {
      setError('Erreur lors du chargement des matières');
    } finally {
      setMatieresLoading(false);
    }
  };

  // ⭐ GESTIONNAIRES DE CHANGEMENT
  const handleAnneeChange = (e) => {
    const anneeId = e.target.value;
    console.log('🔄 [handleAnneeChange] Année sélectionnée:', anneeId);
    setFormData(prev => ({
      ...prev,
      annee_universitaire_id: anneeId,
      // La filiere et le niveau ne sont plus effaces : changer l annee d un module doit
      // pouvoir se faire sans resaisir toute la filiere. Vider ces champs rendait la
      // correction manuelle impossible des qu on touchait l annee.
      filiere_id: prev.filiere_id,
      niveau_id: prev.niveau_id,
      periode_id: prev.periode_id
    }));
  };

  const handleFiliereChange = (e) => {
    const filiereId = e.target.value;
    console.log('🔄 [handleFiliereChange] Filière sélectionnée:', filiereId);
    setFormData(prev => ({
      ...prev,
      filiere_id: filiereId,
      niveau_id: '',
      periode_id: ''
    }));
  };

  const handleNiveauChange = (e) => {
    const niveauId = e.target.value;
    console.log('🔄 [handleNiveauChange] Niveau sélectionné:', niveauId);
    setFormData(prev => ({
      ...prev,
      niveau_id: niveauId,
      periode_id: ''
    }));
  };

  const handlePeriodeChange = (e) => {
    console.log('🔄 [handlePeriodeChange] Période sélectionnée:', e.target.value);
    setFormData(prev => ({
      ...prev,
      periode_id: e.target.value
    }));
  };

  // Bascule un module vers le niveau et la periode de l annee choisie, en une seule action.
  const corrigerNiveauAnnee = (niveauCible) => {
    const periodeCible = periodes
      .filter(p => String(p.niveau_id) === String(niveauCible.niveau_id))
      .sort((a, b) => (a.ordre || 0) - (b.ordre || 0))[0];
    setFormData(prev => ({
      ...prev,
      niveau_id: String(niveauCible.niveau_id),
      periode_id: periodeCible ? String(periodeCible._id) : prev.periode_id
    }));
  };

  const handleSubmit = async () => {
    // ⭐ VALIDATION
    if (!formData.code || !formData.nom) {
      setError('Le code et le nom du module sont obligatoires');
      return;
    }
    
    if (!formData.annee_universitaire_id) {
      setError('Veuillez sélectionner une année universitaire');
      return;
    }
    
    if (!formData.filiere_id) {
      setError('Veuillez sélectionner une filière');
      return;
    }
    
    if (!formData.niveau_id) {
      setError('Veuillez sélectionner un niveau');
      return;
    }
    
    if (!formData.periode_id) {
      setError('Veuillez sélectionner une période');
      return;
    }

    try {
      setLoading(true);
      const headers = getAuthHeaders();
      
      const dataToSend = {
        ...formData,
        credits: parseInt(formData.credits) || 0,
        coefficient: parseFloat(formData.coefficient) || 1
      };
      
      console.log('📤 [Submit] Envoi des données:', dataToSend);
      
      if (modalMode === 'add') {
        await axios.post('/api/modules', dataToSend, { headers });
        setSuccessMessage('Module ajouté avec succès');
      } else {
        await axios.put(`/api/modules/${selectedItem._id}`, dataToSend, { headers });
        setSuccessMessage('Module modifié avec succès');
      }
      await loadData();
      handleCloseModal();
    } catch (err) {
      console.error('❌ [Submit] Erreur:', err);
      setError(err.response?.data?.error || 'Erreur lors de l\'opération');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer définitivement ${item.nom} ?`)) return;
    try {
      await axios.delete(`/api/modules/${item._id}`, { headers: getAuthHeaders() });
      setSuccessMessage('Module supprimé avec succès');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleImport = async () => {
    const { annee_universitaire_id, filiere_id, niveau_id, periode_id, csv, ecraser } = importData;
    if (!annee_universitaire_id || !filiere_id || !niveau_id || !periode_id) {
      setError('Veuillez sélectionner Année, Filière, Niveau et Période');
      return;
    }
    if (!csv.trim()) {
      setError('Veuillez entrer des modules (code;nom)');
      return;
    }
    try {
      const lines = csv.trim().split('\n').filter(Boolean);
      const modules = lines.map(line => {
        const [code, ...rest] = line.split(';');
        return { code: code.trim(), nom: rest.join(';').trim() };
      }).filter(m => m.code && m.nom);
      const res = await axios.post('/api/modules/import', {
        annee_universitaire_id, filiere_id, niveau_id, periode_id,
        modules, ecraser
      }, { headers: getAuthHeaders() });
      setImportResult(res.data);
      setSuccessMessage(`${res.data.created} module(s) importé(s)`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'import');
    }
  };

  const handleCloseImport = () => {
    setImportOpen(false);
    setImportResult(null);
    setImportData({ annee_universitaire_id: '', filiere_id: '', niveau_id: '', periode_id: '', csv: '', ecraser: false });
  };

  const handleJsonFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const json = JSON.parse(evt.target.result);
        const arr = Array.isArray(json) ? json : json.modules || json.module || [];
        if (!Array.isArray(arr) || arr.length === 0) {
          setError('Le fichier JSON doit contenir un tableau de modules');
          return;
        }
        const csvLines = arr.map(m => `${m.code || ''};${m.nom || ''}`).join('\n');
        setImportData(prev => ({ ...prev, csv: csvLines }));
      } catch (err) {
        setError('Fichier JSON invalide : ' + err.message);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError('')}
        message={error}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1f2937' }}>
            📦 Gestion des Modules
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredData.length} module(s) trouvé(s)
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
            Actualiser
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={() => setImportOpen(true)}
            sx={{ color: '#059669', borderColor: '#059669' }}
          >
            Importer
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal('add')}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            Nouveau Module
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par nom, code, période, niveau, filière ou année..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Paper>

      {/* Filtres en cascade */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList color="action" />
            <Typography variant="body2" fontWeight="500" color="text.secondary">Filtres :</Typography>
          </Box>
          <FormControl sx={{ minWidth: 160 }} size="small">
            <InputLabel>Année</InputLabel>
            <Select value={filtreAnnee} label="Année" onChange={(e) => { setFiltreAnnee(e.target.value); setFiltreFiliere(''); setFiltreNiveau(''); setFiltrePeriode(''); setPage(0); }}>
              <MenuItem value="">Toutes</MenuItem>
              {annees.map((a) => (<MenuItem key={a.annee_id} value={a.annee_id}>{a.intitule} {a.estActive ? '⭐' : ''}</MenuItem>))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Filière</InputLabel>
            <Select value={filtreFiliere} label="Filière" onChange={(e) => { setFiltreFiliere(e.target.value); setFiltreNiveau(''); setFiltrePeriode(''); setPage(0); }}>
              <MenuItem value="">Toutes</MenuItem>
              {/* Sans annee selectionnee on propose toutes les filieres, pas seulement celles de
                  l annee courante : une filiere rattachee a 2026/2027 mais dont les modules sont
                  encore marques 2025/2026 devenait impossible a selectionner, donc ses modules
                  ne pouvaient etre ni vus ni corriges a la main. */}
              {fbFilieres.map((f) => (
                <MenuItem key={f.filiere_id} value={f.filiere_id}>
                  {f.libelle || f.code_filiere || f.filiere_id}
                  {f.intitule ? ` — ${f.intitule}` : ''}
                  {!getAnneeIds(filtreAnnee).includes(f.annee_id) && filtreAnnee ? ' ⚠️' : ''}
                </MenuItem>
              ))}
            </Select>
            {filtreAnnee && filtreFiliere && !getAnneeIds(filtreAnnee).includes(getFiliere(filtreFiliere)?.annee_id) && (
              <Typography variant="caption" color="warning.main">
                ⚠️ Cette filière est rattachée à une autre année universitaire
              </Typography>
            )}
          </FormControl>
          <FormControl sx={{ minWidth: 160 }} size="small" disabled={!filtreFiliere}>
            <InputLabel>Niveau</InputLabel>
            <Select value={filtreNiveau} label="Niveau" onChange={(e) => { setFiltreNiveau(e.target.value); setFiltrePeriode(''); setPage(0); }}>
              <MenuItem value="">Tous</MenuItem>
              {fbNiveaux.map((n) => (<MenuItem key={n.niveau_id} value={n.niveau_id}>{n.intitule}</MenuItem>))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 160 }} size="small" disabled={!filtreNiveau}>
            <InputLabel>Période</InputLabel>
            <Select value={filtrePeriode} label="Période" onChange={(e) => { setFiltrePeriode(e.target.value); setPage(0); }}>
              <MenuItem value="">Toutes</MenuItem>
              {fbPeriodes.map((p) => (<MenuItem key={p._id} value={p._id}>{p.nom}</MenuItem>))}
            </Select>
          </FormControl>
          {(filtreAnnee || filtreFiliere || filtreNiveau || filtrePeriode) && (
            <Button size="small" variant="outlined" onClick={() => { setFiltreAnnee(''); setFiltreFiliere(''); setFiltreNiveau(''); setFiltrePeriode(''); }} startIcon={<Clear />}>Effacer</Button>
          )}
        </Stack>
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ background: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nom</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Période</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Niveau</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Filière</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Année</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Crédits</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Matières</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => {
              const periode = getPeriode(item.periode_id);
              const niveau = getNiveau(item.niveau_id);
              const filiere = getFiliere(item.filiere_id);
              const annee = getAnnee(item.annee_universitaire_id);
              return (
                <TableRow key={item._id} hover>
                  <TableCell sx={{ fontWeight: 600, color: '#667eea', fontFamily: 'monospace' }}>{item.code}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{item.nom}</TableCell>
                  <TableCell>
                    <Chip label={periode?.nom || '-'} size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e' }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={niveau?.intitule || '-'} size="small" sx={{ bgcolor: '#f3e8ff', color: '#7c3aed' }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={filiere?.libelle || filiere?.intitule || filiere?.filiere_id || '-'} size="small" sx={{ bgcolor: '#e0e7ff', color: '#4338ca' }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={annee?.intitule || '-'} size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af' }} />
                  </TableCell>
                  <TableCell>{item.credits}</TableCell>
                  <TableCell>
                    <Button size="small" variant="text"
                      onClick={() => loadMatieresForModule(item)}
                      startIcon={<SubjectIcon fontSize="small" />}
                      sx={{ textTransform: 'none', color: '#8b5cf6' }}>
                      Voir
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.estActif ? '✅ Actif' : '❌ Inactif'}
                      size="small"
                      sx={{ bgcolor: item.estActif ? '#dcfce7' : '#fee2e2', color: item.estActif ? '#166534' : '#991b1b' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Modifier">
                        <IconButton size="small" color="warning" onClick={() => handleOpenModal('edit', item)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredData.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">Aucun module trouvé</Typography>
                  {modulesAnneeDifferente.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="warning">
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {modulesAnneeDifferente.length} module(s) existent pour cette filière mais sont enregistrés sous{' '}
                          <strong>{getAnnee(modulesAnneeDifferente[0].annee_universitaire_id)?.intitule || modulesAnneeDifferente[0].annee_universitaire_id}</strong>, pas {getAnnee(filtreAnnee)?.intitule}.
                          C’est pourquoi la liste est vide.
                        </Typography>
                        <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => { setFiltreAnnee(''); setPage(0); }}>
                          Afficher ces modules pour les corriger
                        </Button>
                      </Alert>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredData.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        labelRowsPerPage="Lignes par page"
        sx={{ mt: 2 }}
      />

      {/* ============================================ */}
      {/* MODAL AJOUT/MODIFICATION AVEC SÉLECTION EN CASCADE */}
      {/* ============================================ */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Book sx={{ color: '#667eea' }} />
            <Typography variant="h6">
              {modalMode === 'add' ? '➕ Ajouter un module' : '✏️ Modifier le module'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* Code et Nom */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Code *"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  margin="normal"
                  required
                  placeholder="ex: MOD-001"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nom *"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  margin="normal"
                  required
                  placeholder="ex: Programmation Web"
                />
              </Grid>

              {/* ⭐ SÉLECTION EN CASCADE - ANNÉE */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Année Universitaire *</InputLabel>
                  <Select
                    value={formData.annee_universitaire_id}
                    onChange={handleAnneeChange}
                    label="Année Universitaire *"
                  >
                    <MenuItem value="">Sélectionner une année</MenuItem>
                    {annees && annees.length > 0 ? (
                      annees.map((a) => (
                        <MenuItem key={a.annee_id} value={a.annee_id}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CalendarMonth fontSize="small" />
                            <span>{a.intitule}</span>
                            {a.estActive && (
                              <Chip label="Active" size="small" color="success" />
                            )}
                          </Stack>
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>Aucune année disponible</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {/* ⭐ SÉLECTION EN CASCADE - FILIÈRE */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Filière *</InputLabel>
                  <Select
                    value={formData.filiere_id}
                    onChange={handleFiliereChange}
                    label="Filière *"
                  >
                    <MenuItem value="">
                      {formData.annee_universitaire_id
                        ? 'Sélectionner une filière'
                        : 'Sélectionnez d\'abord une année (recommandé)'}
                    </MenuItem>
                    {formFiltredFilieresAvecCourante && formFiltredFilieresAvecCourante.length > 0 ? (
                      formFiltredFilieresAvecCourante.map((f) => (
                        <MenuItem key={f.filiere_id || f._id} value={f.filiere_id}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <School fontSize="small" />
                            <span>{f.libelle || f.intitule || f.filiere_id}</span>
                            {f.libelle && f.intitule && (
                              <Typography variant="caption" color="text.secondary">{f.intitule}</Typography>
                            )}
                          </Stack>
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>Aucune filière disponible</MenuItem>
                    )}
                  </Select>
                </FormControl>
                {formData.annee_universitaire_id && formFiltredFilieresAvecCourante.length === 0 && (
                  <Typography variant="caption" color="warning.main">
                    ⚠️ Aucune filière trouvée pour cette année
                  </Typography>
                )}
              </Grid>

              {/* ⭐ SÉLECTION EN CASCADE - NIVEAU (dépend de la filière) */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth margin="normal" required disabled={!formData.filiere_id}>
                  <InputLabel>Niveau *</InputLabel>
                  <Select
                    value={formData.niveau_id}
                    onChange={handleNiveauChange}
                    label="Niveau *"
                  >
                    <MenuItem value="">
                      {!formData.filiere_id 
                        ? 'Sélectionnez d\'abord une filière' 
                        : 'Sélectionner un niveau'}
                    </MenuItem>
                    {formFiltredNiveaux && formFiltredNiveaux.length > 0 ? (
                      formFiltredNiveaux.map((n) => (
                        <MenuItem key={n.niveau_id || n._id} value={n.niveau_id}>
                          {n.intitule}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>Aucun niveau disponible</MenuItem>
                    )}
                  </Select>
                </FormControl>
                {formData.filiere_id && formFiltredNiveaux.length === 0 && (
                  <Typography variant="caption" color="warning.main">
                    ⚠️ Aucun niveau trouvé pour cette filière
                  </Typography>
                )}
              </Grid>

              {/* ⭐ SÉLECTION EN CASCADE - PÉRIODE (dépend du niveau) */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" required disabled={!formData.niveau_id}>
                  <InputLabel>Période *</InputLabel>
                  <Select
                    value={formData.periode_id}
                    onChange={handlePeriodeChange}
                    label="Période *"
                  >
                    <MenuItem value="">
                      {!formData.niveau_id 
                        ? 'Sélectionnez d\'abord un niveau' 
                        : 'Sélectionner une période'}
                    </MenuItem>
                    {formFiltredPeriodes && formFiltredPeriodes.length > 0 ? (
                      formFiltredPeriodes.map((p) => (
                        <MenuItem key={p._id} value={p._id}>
                          {p.nom}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>Aucune période disponible</MenuItem>
                    )}
                  </Select>
                </FormControl>
                {formData.niveau_id && formFiltredPeriodes.length === 0 && (
                  <Typography variant="caption" color="warning.main">
                    ⚠️ Aucune période trouvée pour ce niveau
                  </Typography>
                )}
              </Grid>

              {/* Responsable */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Responsable"
                  value={formData.responsable}
                  onChange={(e) => setFormData({ ...formData, responsable: e.target.value })}
                  margin="normal"
                  placeholder="Nom du responsable"
                />
              </Grid>

              {/* Crédits et Coefficient */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Crédits"
                  type="number"
                  value={formData.credits}
                  onChange={(e) => setFormData({ ...formData, credits: parseInt(e.target.value) || 0 })}
                  margin="normal"
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Coefficient"
                  type="number"
                  value={formData.coefficient}
                  onChange={(e) => setFormData({ ...formData, coefficient: parseFloat(e.target.value) || 1 })}
                  margin="normal"
                  InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextareaAutosize
                  minRows={3}
                  placeholder="Description du module..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    marginTop: '8px',
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    resize: 'vertical'
                  }}
                />
              </Grid>

              {/* Statut */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.estActif ? 'actif' : 'inactif'}
                    onChange={(e) => setFormData({ ...formData, estActif: e.target.value === 'actif' })}
                    label="Statut"
                  >
                    <MenuItem value="actif">✅ Actif</MenuItem>
                    <MenuItem value="inactif">❌ Inactif</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Récapitulatif des sélections */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    <FilterList fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Récapitulatif des sélections
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip 
                      label={`Année: ${annees.find(a => a.annee_id === formData.annee_universitaire_id)?.intitule || 'Non sélectionnée'}`}
                      size="small"
                      variant="outlined"
                      icon={<CalendarMonth />}
                    />
                    <Chip 
                      label={`Filière: ${filieres.find(f => String(f.filiere_id) === String(formData.filiere_id))?.libelle || filieres.find(f => String(f.filiere_id) === String(formData.filiere_id))?.intitule || 'Non sélectionnée'}`}
                      size="small"
                      variant="outlined"
                      icon={<School />}
                    />
                    <Chip 
                      label={`Niveau: ${niveaux.find(n => String(n.niveau_id) === String(formData.niveau_id))?.intitule || 'Non sélectionné'}`}
                      size="small"
                      variant="outlined"
                      icon={<Book />}
                    />
                    <Chip 
                      label={`Période: ${periodes.find(p => String(p._id) === String(formData.periode_id))?.nom || 'Non sélectionnée'}`}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                  {/* Une filiere et une annee qui ne se repondent pas est la cause des listes
                      vides les plusjfrequentes : on le dit plutot que de laisser deviner. */}
                  {formData.annee_universitaire_id && formData.filiere_id && filiereCourante && !getAnneeIds(formData.annee_universitaire_id).includes(filiereCourante.annee_id) && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      La filière <strong>{filiereCourante.libelle || filiereCourante.intitule}</strong> est rattachée à{' '}
                      <strong>{getAnnee(filiereCourante.annee_id)?.intitule || filiereCourante.annee_id}</strong>, pas à l’année sélectionnée.
                      Les modules enregistrés ici apparaîtront dans cette autre année.
                    </Alert>
                  )}
                  {formData.annee_universitaire_id && formData.niveau_id && niveauCourant && !getAnneeIds(formData.annee_universitaire_id).map(String).includes(String(niveauCourant.annee_id)) && niveauEquivalentAnnee && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Le niveau « {niveauCourant.intitule} » sélectionné appartient à {getAnnee(niveauCourant.annee_id)?.intitule || 'une autre année'}. Pour l’année sélectionnée, le niveau équivalent est{' '}
                      <strong>{niveauEquivalentAnnee.niveau_id} – {niveauEquivalentAnnee.intitule}</strong>, avec les périodes{' '}
                      {sansDoublon(periodes.filter(p => memeId(p.niveau_id, niveauEquivalentAnnee.niveau_id))).map(p => p.nom).join(' / ') || 'à vérifier'}.
                      <Box sx={{ mt: 1 }}>
                        <Button size="small" variant="outlined" color="warning" startIcon={<Refresh />} onClick={() => corrigerNiveauAnnee(niveauEquivalentAnnee)}>
                          Appliquer le niveau {niveauEquivalentAnnee.niveau_id} et sa période
                        </Button>
                      </Box>
                    </Alert>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} startIcon={<Cancel />}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            startIcon={<Save />} 
            sx={{ bgcolor: '#667eea' }}
            disabled={
              !formData.code || 
              !formData.nom || 
              !formData.annee_universitaire_id || 
              !formData.filiere_id || 
              !formData.niveau_id || 
              !formData.periode_id
            }
          >
            {modalMode === 'add' ? 'Ajouter' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Matières Dialog */}
      <Dialog open={matieresDialogOpen} onClose={() => setMatieresDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SubjectIcon sx={{ color: '#8b5cf6' }} />
            Matières du module : {matieresModule?.code} - {matieresModule?.nom}
          </Box>
        </DialogTitle>
        <DialogContent>
          {matieresLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : matieresList.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">Aucune matière rattachée à ce module</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Nom</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Crédits</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Volume Horaire</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matieresList.map((m) => {
                    const vh = m.volumeHoraire || {};
                    const total = (vh.cours || 0) + (vh.td || 0) + (vh.tp || 0);
                    return (
                      <TableRow key={m._id} hover>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{m.code}</TableCell>
                        <TableCell>{m.nom}</TableCell>
                        <TableCell>{m.credits}</TableCell>
                        <TableCell>{total}h</TableCell>
                        <TableCell>
                          <Chip label={m.estActif ? 'Actif' : 'Inactif'} size="small"
                            color={m.estActif ? 'success' : 'default'} variant="outlined" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatieresDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importOpen} onClose={handleCloseImport} maxWidth="md" fullWidth>
        <DialogTitle>📥 Importer des modules</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
              1. Sélectionnez le contexte (les modules seront rattachés à ces éléments)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Année *</InputLabel>
                  <Select
                    value={importData.annee_universitaire_id}
                    onChange={(e) => setImportData({ ...importData, annee_universitaire_id: e.target.value, filiere_id: '', niveau_id: '', periode_id: '' })}
                    label="Année *"
                  >
                    <MenuItem value="">Sélectionner</MenuItem>
                    {annees.map((a) => (
                      <MenuItem key={a.annee_id} value={a.annee_id}>{a.intitule} {a.estActive ? '⭐' : ''}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" disabled={!importData.annee_universitaire_id}>
                  <InputLabel>Filière *</InputLabel>
                  <Select
                    value={importData.filiere_id}
                    onChange={(e) => setImportData({ ...importData, filiere_id: e.target.value, niveau_id: '', periode_id: '' })}
                    label="Filière *"
                  >
                    <MenuItem value="">Sélectionner</MenuItem>
                    {filieres.filter(f => getAnneeIds(importData.annee_universitaire_id).includes(f.annee_id)).map((f) => (
                      <MenuItem key={f.filiere_id} value={f.filiere_id}>{f.code_filiere || f.libelle} - {f.intitule}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" disabled={!importData.filiere_id}>
                  <InputLabel>Niveau *</InputLabel>
                  <Select
                    value={importData.niveau_id}
                    onChange={(e) => setImportData({ ...importData, niveau_id: e.target.value, periode_id: '' })}
                    label="Niveau *"
                  >
                    <MenuItem value="">Sélectionner</MenuItem>
                    {niveaux.filter(n => filiereNiveaux.some(fn => memeId(fn.niveau_id, n.niveau_id) && memeId(fn.filiere_id, importData.filiere_id))).map((n) => (
                      <MenuItem key={n.niveau_id} value={n.niveau_id}>{n.intitule}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" disabled={!importData.niveau_id}>
                  <InputLabel>Période *</InputLabel>
                  <Select
                    value={importData.periode_id}
                    onChange={(e) => setImportData({ ...importData, periode_id: e.target.value })}
                    label="Période *"
                  >
                    <MenuItem value="">Sélectionner</MenuItem>
                    {sansDoublon(periodes.filter(p => memeId(p.niveau_id, importData.niveau_id))).map((p) => (
                      <MenuItem key={p._id} value={p._id}>{p.nom}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
              2. Entrez les modules (CSV: <code>code;nom</code> ou fichier JSON)
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<UploadFile />}
                onClick={() => fileInputRef.current?.click()}
              >
                Charger fichier JSON
              </Button>
              <input type="file" accept=".json" hidden ref={fileInputRef} onChange={handleJsonFile} />
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Format JSON : <code>{'[{"code":"M301","nom":"..."}]'}</code>
              </Typography>
            </Stack>

            <TextareaAutosize
              minRows={8}
              placeholder={'M301;Module Name 1\nM302;Module Name 2\nM303;Module Name 3'}
              value={importData.csv}
              onChange={(e) => setImportData({ ...importData, csv: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                fontFamily: 'monospace',
                fontSize: '13px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                resize: 'vertical'
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Lignes: {importData.csv.trim() ? importData.csv.trim().split('\n').filter(Boolean).length : 0} module(s) détecté(s)
            </Typography>

            <FormControlLabel
              control={<Checkbox checked={importData.ecraser} onChange={(e) => setImportData({ ...importData, ecraser: e.target.checked })} />}
              label="Remplacer les modules existants (même code même niveau)"
              sx={{ mt: 2 }}
            />

            {importResult && (
              <Paper sx={{ p: 2, mt: 2, bgcolor: '#f0fdf4', borderRadius: 2 }}>
                <Typography variant="subtitle2" color="#166534">Résultat de l'import</Typography>
                <Typography variant="body2">✅ {importResult.created} créé(s)</Typography>
                <Typography variant="body2">⏭️ {importResult.skipped} ignoré(s)</Typography>
                {importResult.errors?.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="#991b1b">❌ Erreurs :</Typography>
                    {importResult.errors.map((e, i) => <Typography key={i} variant="caption" display="block">{e}</Typography>)}
                  </Box>
                )}
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImport} startIcon={<Cancel />}>Fermer</Button>
          <Button
            onClick={handleImport}
            variant="contained"
            startIcon={<CloudUpload />}
            disabled={!importData.annee_universitaire_id || !importData.filiere_id || !importData.niveau_id || !importData.periode_id || !importData.csv.trim()}
            sx={{ bgcolor: '#059669' }}
          >
            Importer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Modules;


